/**
 * G18 "Execution efficiency" metrics per project, from the encrypted run usage reports the web
 * already loads. Pure, no I/O. Everything is labelled with what it measures:
 *  - tokens per model tier come from the report; cost per tier is the run's cost split by token
 *    share (attributed, not billed per tier).
 *  - repeated full validations are the evidence entries of D3 findings (each is one flagged run
 *    of a full test/build), per landed run.
 *  - exploration is the estimated tokens subagents spent without a target (D2) over all subagent
 *    tokens.
 */
import type { RunEfficiency } from '@viko/protocol'

export const TIERS = ['fast', 'standard', 'deep', 'unknown'] as const
export type Tier = (typeof TIERS)[number]

export interface ExecutionRun {
  runId: string
  projectId: string
  projectName?: string
  updatedAt: number
  /** Run cost in USD (reported, else estimated); undefined when neither exists. */
  usd?: number
  landed?: boolean
  subagentTokens?: number
  efficiency?: RunEfficiency
}

export interface TierRow {
  tier: Tier
  tokens: number
  /** Share of all tier tokens, 0..1. */
  share: number
  attributedUsd: number | null
}

export interface FindingWeek {
  /** Week start (ms, 7-day buckets counted back from `now`). */
  weekStart: number
  findings: number
}

export interface ProjectExecution {
  projectId: string
  name: string
  runs: number
  runsWithReport: number
  tiers: TierRow[]
  totalTokens: number
  landedRuns: number
  fullValidations: number
  /** null when no run landed. */
  fullValidationsPerLanded: number | null
  subagentTokens: number
  explorationTokens: number
  /** null when there were no subagent tokens. */
  explorationShare: number | null
  findingsTotal: number
  findingsById: Record<'D1' | 'D2' | 'D3' | 'D4', number>
  weeks: FindingWeek[]
  runIdsWithFindings: string[]
}

const DAY = 86_400_000

export function executionEfficiency(
  runs: readonly ExecutionRun[],
  opts: { now: number; windowDays: number },
): ProjectExecution[] {
  const from = opts.now - opts.windowDays * DAY
  const nWeeks = Math.max(1, Math.ceil(opts.windowDays / 7))
  const by = new Map<string, ExecutionRun[]>()
  for (const r of runs) {
    if (r.updatedAt < from) continue
    by.set(r.projectId, [...(by.get(r.projectId) ?? []), r])
  }
  const out: ProjectExecution[] = []
  for (const [projectId, list] of by) {
    const tok: Record<Tier, number> = { fast: 0, standard: 0, deep: 0, unknown: 0 }
    const usd: Record<Tier, number> = { fast: 0, standard: 0, deep: 0, unknown: 0 }
    let usdKnown = false
    let withReport = 0
    let landed = 0
    let full = 0
    let sub = 0
    let explore = 0
    const byId = { D1: 0, D2: 0, D3: 0, D4: 0 }
    const weeks = Array.from({ length: nWeeks }, (_, i) => ({
      weekStart: opts.now - (nWeeks - i) * 7 * DAY,
      findings: 0,
    }))
    const withFindings: string[] = []
    for (const r of list) {
      if (r.landed) landed++
      sub += r.subagentTokens ?? 0
      const e = r.efficiency
      if (!e) continue
      withReport++
      const total = TIERS.reduce((s, t) => s + e.tokensByTier[t], 0)
      for (const t of TIERS) {
        tok[t] += e.tokensByTier[t]
        if (r.usd !== undefined && total > 0) {
          usd[t] += (r.usd * e.tokensByTier[t]) / total
          usdKnown = true
        }
      }
      if (e.findings.length) withFindings.push(r.runId)
      for (const f of e.findings) {
        byId[f.id]++
        if (f.id === 'D3') full += f.evidence.length
        if (f.id === 'D2') explore += f.estSavingTokens ?? 0
        const w = Math.min(
          nWeeks - 1,
          Math.max(0, Math.floor((r.updatedAt - weeks[0]!.weekStart) / (7 * DAY))),
        )
        weeks[w]!.findings++
      }
    }
    const totalTokens = TIERS.reduce((s, t) => s + tok[t], 0)
    out.push({
      projectId,
      name: list.find((r) => r.projectName)?.projectName ?? projectId,
      runs: list.length,
      runsWithReport: withReport,
      tiers: TIERS.map((t) => ({
        tier: t,
        tokens: tok[t],
        share: totalTokens ? tok[t] / totalTokens : 0,
        attributedUsd: usdKnown ? usd[t] : null,
      })),
      totalTokens,
      landedRuns: landed,
      fullValidations: full,
      fullValidationsPerLanded: landed ? full / landed : null,
      subagentTokens: sub,
      explorationTokens: explore,
      explorationShare: sub ? Math.min(1, explore / sub) : null,
      findingsTotal: byId.D1 + byId.D2 + byId.D3 + byId.D4,
      findingsById: byId,
      weeks,
      runIdsWithFindings: withFindings,
    })
  }
  return out.filter((p) => p.runsWithReport > 0).sort((a, b) => b.totalTokens - a.totalTokens)
}
